import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/pageHeader";
import { useTranslation } from "../../../i18n/useTranslation";

const QUESTIONS = [
  { key: "environment", q: "What environment will the device operate in?", options: ["Indoor office", "Warehouse / industrial", "Outdoor / field", "Mixed"] },
  { key: "formFactor", q: "What form factor do you need?", options: ["Handheld scanner", "Mobile computer", "Tablet", "Wearable"] },
  { key: "dataCapture", q: "What kind of data capture matters most?", options: ["Barcode / 1D-2D", "RFID", "Image capture", "None needed"] },
  { key: "connectivity", q: "What connectivity does it need?", options: ["Wi-Fi only", "Cellular (4G/5G)", "Wi-Fi + Cellular", "Bluetooth / short-range only"] },
  { key: "os", q: "Any operating system preference?", options: ["Android", "iOS", "Windows", "No preference"] },
  { key: "usage", q: "How intense is daily usage?", options: ["Light — a few hours", "Moderate — a shift", "Heavy — multi-shift, dropped often"] },
  { key: "budget", q: "What's the approximate budget per unit?", options: ["Under $300", "$300–$700", "$700–$1,500", "$1,500+"] },
  { key: "volume", q: "Roughly how many units?", options: ["1–10", "11–50", "51–250", "250+"] },
  { key: "condition", q: "New or refurbished?", options: ["New only", "Open to certified refurbished", "Refurbished preferred"] },
  { key: "timeline", q: "What's your timeline?", options: ["ASAP", "Within a month", "1–3 months", "Just exploring"] },
];

const buildRecommendation = (answers) => {
  const category =
    answers.environment === "Warehouse / industrial" || answers.usage === "Heavy — multi-shift, dropped often"
      ? "rugged mobile computers"
      : answers.formFactor === "Tablet"
      ? "enterprise tablets"
      : answers.formFactor === "Wearable"
      ? "wearable scanners"
      : "handheld scanners";

  const condition =
    answers.condition === "Refurbished preferred" || answers.budget === "Under $300"
      ? "certified refurbished"
      : answers.condition === "New only"
      ? "new"
      : "a mix of new and certified refurbished";

  return `Based on your answers — a ${answers.environment?.toLowerCase()} environment, ${answers.usage?.toLowerCase()} usage, and a budget around ${answers.budget} — we'd point you toward ${condition} ${category}, in the ${answers.volume} unit range. For a ${answers.timeline?.toLowerCase()} timeline, our team can walk through specific models on a quick call.`;
};

const MarketplaceCompare = () => {
  const { t } = useTranslation();
  const h = t("deviceworks.pageHeaders.compare");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;

  const select = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));
  const restart = () => { setStep(0); setAnswers({}); };

  return (
    <div>
      <PageHeader eyebrow={h.eyebrow} title={h.title} description={h.description} />

      <div className="max-w-xl mx-auto px-5 py-16">
        {!done && (
          <>
            <div className="w-full h-1 rounded mb-8" style={{ backgroundColor: "var(--dw-line)" }}>
              <div
                className="h-1 rounded transition-all duration-300"
                style={{ width: `${(step / QUESTIONS.length) * 100}%`, backgroundColor: "var(--dw-accent)" }}
              />
            </div>
            <div className="dw-mono text-xs mb-2" style={{ color: "var(--dw-text-faint)" }}>
              Question {step + 1} of {QUESTIONS.length}
            </div>
            <h3 className="pb-5">{QUESTIONS[step].q}</h3>
            <div className="flex flex-col gap-2">
              {QUESTIONS[step].options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => select(QUESTIONS[step].key, opt)}
                  className="dw-card text-left py-3"
                >
                  {opt}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button type="button" onClick={back} className="dw-link text-sm pt-6 inline-block">← Back</button>
            )}
          </>
        )}

        {done && (
          <div className="dw-card">
            <div className="dw-eyebrow mb-2">Your Recommendation</div>
            <p className="dw-mini">{buildRecommendation(answers)}</p>
            <div className="flex gap-3 pt-5 flex-wrap">
              <Link to="/solutions/deviceworks/get-a-quote" className="dw-btn">Get a Quote</Link>
              <button type="button" onClick={restart} className="dw-btn-outline">Start Over</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceCompare;
